/**
 * Line: one number line that never ends.
 *
 * Slide it, pinch it, and the ticks re-label themselves — ones become tens
 * become thousands become millions, all on the same line. It is the only toy
 * here where a child can feel how far away a million actually is, because
 * getting there means scrolling past everything in between.
 */

import { useRef, useState } from 'preact/hooks';
import type { ComponentChildren, JSX } from 'preact';
import { Home, MapPin, RotateCcw } from 'lucide-preact';
import { token } from '../../engine/palette.ts';
import { play } from '../../engine/audio.ts';
import { isTap, useSurface } from '../../hooks/useSurface.ts';
import { groupDigits, roughName } from '../../lib/bignum.ts';
import { Chip, Coach, ToyButton } from '../../ui/controls.tsx';

interface Props {
  onExit: () => void;
  tabs: ComponentChildren;
}

/** Numbers per pixel; smaller means more zoomed in. */
const START_SPAN = 20;
const MIN_SPAN = 0.002;
const MAX_SPAN = 4e12;

interface View {
  /** The number at the centre of the screen. */
  centre: number;
  /** How many numbers fit across one pixel. */
  span: number;
}

/** A tick step of 1, 2 or 5 times a power of ten — how rulers are always ruled. */
function niceStep(rough: number): number {
  const power = 10 ** Math.floor(Math.log10(rough));
  const scaled = rough / power;
  if (scaled <= 1) return power;
  if (scaled <= 2) return 2 * power;
  if (scaled <= 5) return 5 * power;
  return 10 * power;
}

export function NumberLineToy({ onExit, tabs }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const viewRef = useRef<View>({ centre: 10, span: START_SPAN / 320 });
  const pointsRef = useRef(new Map<number, { x: number; y: number }>());
  const pinchRef = useRef<{ a: number; b: number; prevA: number; prevB: number } | null>(null);
  const [pin, setPin] = useState<number | null>(null);
  const [centreLabel, setCentreLabel] = useState('10');

  useSurface(canvasRef, {
    onFrame: (ctx, _dt, { width, height }) => {
      const view = viewRef.current;
      const y = height * 0.52;

      ctx.fillStyle = token('--paper');
      ctx.fillRect(0, 0, width, height);

      const numberAt = (px: number) => view.centre + (px - width / 2) * view.span;
      const pixelAt = (n: number) => width / 2 + (n - view.centre) / view.span;

      // Two rulers: fine ticks, and a labelled one ten times coarser.
      const step = niceStep(view.span * 64);
      const big = step * (step * 5 >= 1 ? 5 : 10);

      ctx.strokeStyle = token('--paper-line');
      ctx.lineWidth = 2;
      const first = Math.floor(numberAt(0) / step) * step;
      const last = numberAt(width);
      for (let n = first; n <= last; n += step) {
        const x = pixelAt(n);
        const major = Math.abs(n / big - Math.round(n / big)) < 1e-6;
        ctx.beginPath();
        ctx.moveTo(x, y - (major ? 22 : 10));
        ctx.lineTo(x, y + (major ? 22 : 10));
        ctx.stroke();
      }

      // The line itself.
      ctx.strokeStyle = token('--ink');
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();

      // Labels only on the coarse ticks, so they never crowd.
      ctx.save();
      ctx.fillStyle = token('--ink-soft');
      ctx.textAlign = 'center';
      ctx.font = `700 ${Math.min(16, width * 0.042)}px ui-rounded, system-ui, sans-serif`;
      const firstBig = Math.ceil(numberAt(0) / big) * big;
      for (let n = firstBig; n <= last; n += big) {
        const value = Math.round(n * 1000) / 1000;
        ctx.fillText(labelFor(value), pixelAt(n), y + 44);
      }
      ctx.restore();

      // Where zero is, if it is anywhere near.
      const zeroX = pixelAt(0);
      if (zeroX > -40 && zeroX < width + 40) {
        ctx.save();
        ctx.strokeStyle = token('--crayon-red');
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(zeroX, y - 34);
        ctx.lineTo(zeroX, y + 34);
        ctx.stroke();
        ctx.restore();
      }

      // The dropped pin, wherever it has ended up.
      if (pin !== null) {
        const px = pixelAt(pin);
        ctx.save();
        ctx.strokeStyle = token('--crayon-purple');
        ctx.fillStyle = token('--crayon-purple');
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(px, y);
        ctx.lineTo(px, y - 62);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(px, y - 66, 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      // The crosshair in the middle is what the readout is reading.
      ctx.save();
      ctx.strokeStyle = token('--crayon-teal');
      ctx.lineWidth = 3;
      ctx.setLineDash([6, 6]);
      ctx.beginPath();
      ctx.moveTo(width / 2, y - 80);
      ctx.lineTo(width / 2, y + 80);
      ctx.stroke();
      ctx.restore();
    },

    onDown: (p) => {
      pointsRef.current.set(p.id, { x: p.pos.x, y: p.pos.y });
      if (pointsRef.current.size === 2) {
        const [a, b] = [...pointsRef.current.entries()];
        pinchRef.current = { a: a[0], b: b[0], prevA: a[1].x, prevB: b[1].x };
      }
    },

    onMove: (p, { width }) => {
      const previous = pointsRef.current.get(p.id);
      if (!previous) return;
      const view = viewRef.current;
      const pinching = pinchRef.current;
      pointsRef.current.set(p.id, { x: p.pos.x, y: p.pos.y });

      if (pinching) {
        const a = pointsRef.current.get(pinching.a);
        const b = pointsRef.current.get(pinching.b);
        if (!a || !b) return;
        const before = Math.abs(pinching.prevA - pinching.prevB);
        const after = Math.abs(a.x - b.x);
        if (before > 8 && after > 8) {
          // Keep the number under the midpoint where the fingers left it.
          const midBefore = (pinching.prevA + pinching.prevB) / 2;
          const midAfter = (a.x + b.x) / 2;
          const anchor = view.centre + (midBefore - width / 2) * view.span;
          view.span = Math.max(MIN_SPAN, Math.min(MAX_SPAN, view.span * (before / after)));
          view.centre = anchor - (midAfter - width / 2) * view.span;
        }
        pinching.prevA = a.x;
        pinching.prevB = b.x;
      } else {
        view.centre -= (p.pos.x - previous.x) * view.span;
      }
      setCentreLabel(labelFor(roundForSpan(view.centre, view.span)));
    },

    onUp: (p, { width }) => {
      pointsRef.current.delete(p.id);
      if (pinchRef.current && pointsRef.current.size < 2) pinchRef.current = null;
      if (isTap(p)) {
        const view = viewRef.current;
        setPin(roundForSpan(view.centre + (p.pos.x - width / 2) * view.span, view.span));
        play('pop', 0.5);
      }
    },

    onWheel: (w, { width }) => {
      const view = viewRef.current;
      const anchor = view.centre + (w.pos.x - width / 2) * view.span;
      view.span = Math.max(MIN_SPAN, Math.min(MAX_SPAN, view.span * Math.exp(w.deltaY * 0.0016)));
      view.centre = anchor - (w.pos.x - width / 2) * view.span;
      setCentreLabel(labelFor(roundForSpan(view.centre, view.span)));
    },
  });

  const jumpTo = (n: number) => {
    const view = viewRef.current;
    view.centre = n;
    view.span = Math.max(MIN_SPAN, (Math.abs(n) || 10) / 160);
    setCentreLabel(labelFor(n));
    play('sparkle', 0.6);
  };

  const reset = () => {
    viewRef.current = { centre: 10, span: START_SPAN / 320 };
    setPin(null);
    setCentreLabel('10');
    play('whoosh', 0.6);
  };

  const centreValue = roundForSpan(viewRef.current.centre, viewRef.current.span);
  const rough = Math.abs(centreValue) >= 1000 ? roughName(BigInt(Math.round(centreValue))) : null;

  return (
    <div class="screen">
      <div class="stage">
        <canvas ref={canvasRef} aria-label="Number line" />
        <div class="topbar">
          <ToyButton round label="Home" hideLabel onClick={onExit}>
            <Home size={24} strokeWidth={2.5} />
          </ToyButton>
          <div class="topbar__group">
            <ToyButton round label="Drop a pin here" hideLabel onClick={() => jumpTo(centreValue)}>
              <MapPin size={24} strokeWidth={2.5} />
            </ToyButton>
            <ToyButton round label="Back to the start" hideLabel onClick={reset}>
              <RotateCcw size={24} strokeWidth={2.5} />
            </ToyButton>
          </div>
        </div>

        <p class="readout">
          <strong>{centreLabel}</strong>
          <span>{rough ? `about ${rough}` : 'slide the line, pinch to zoom'}</span>
        </p>

        {pin !== null && <Coach>Your pin is at {labelFor(pin)}</Coach>}
      </div>

      <div class="dock">
        {tabs}
        <div class="dock__row dock__row--scroll" role="group" aria-label="Jump to a number">
          {[10, 100, 1_000, 10_000, 1_000_000, 1_000_000_000].map((n) => (
            <Chip key={n} label={labelFor(n)} onClick={() => jumpTo(n)} />
          ))}
        </div>
      </div>
    </div>
  );
}

/** Do not claim more precision than the zoom can show. */
function roundForSpan(n: number, span: number): number {
  const step = niceStep(span * 8);
  return Math.round(n / step) * step;
}

function labelFor(n: number): string {
  if (!Number.isFinite(n)) return '0';
  if (Number.isInteger(n)) return groupDigits(BigInt(n));
  return String(Math.round(n * 1000) / 1000);
}
